import type React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HARD_MAX_CONCURRENCY } from '@tekvoice/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';
import {
  ReadOnlyNotice,
  SETTINGS_QUERY_KEY,
  SettingSlider,
  useSettingsDraft,
} from '@/pages/settings/settings-form';

/** SPEC §8 — concurrency and import limits, all bounded sliders. */
export function GenerationSettingsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { draft, setDraft, isLoading, readOnly, dirty } = useSettingsDraft('generation');

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to save');
      return api.settings.update({ generation: draft });
    },
    onSuccess: async (result) => {
      setDraft(result.data.generation);
      toast.success('Generation settings saved');
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading || !draft) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <ReadOnlyNotice readOnly={readOnly} />
      <Card>
        <CardHeader>
          <CardTitle>Generation</CardTitle>
          <CardDescription>
            The worker runs in-process. Concurrency is capped at {HARD_MAX_CONCURRENCY} by the plan.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingSlider
            id="concurrency"
            label="Concurrency"
            description="Parallel ElevenLabs requests."
            value={draft.concurrency}
            min={1}
            max={HARD_MAX_CONCURRENCY}
            step={1}
            disabled={readOnly}
            onChange={(value) => setDraft((previous) => (previous ? { ...previous, concurrency: value } : previous))}
          />
          <SettingSlider
            id="max-rows"
            label="Max rows per import"
            value={draft.maxRowsPerImport}
            min={50}
            max={5000}
            step={50}
            disabled={readOnly}
            onChange={(value) =>
              setDraft((previous) => (previous ? { ...previous, maxRowsPerImport: value } : previous))
            }
          />
          <SettingSlider
            id="max-cell-chars"
            label="Max characters per cell"
            description="Longer cells reject the whole import."
            value={draft.maxCellChars}
            min={100}
            max={5000}
            step={50}
            disabled={readOnly}
            onChange={(value) =>
              setDraft((previous) => (previous ? { ...previous, maxCellChars: value } : previous))
            }
          />
          <SettingSlider
            id="max-attempts"
            label="Max attempts"
            description="Retries on 429/5xx/timeouts only, with exponential backoff."
            value={draft.maxAttempts}
            min={1}
            max={10}
            step={1}
            disabled={readOnly}
            onChange={(value) =>
              setDraft((previous) => (previous ? { ...previous, maxAttempts: value } : previous))
            }
          />
          {!readOnly ? (
            <div className="flex items-center gap-3">
              <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save generation settings'}
              </Button>
              {dirty ? <span className="text-sm text-muted-foreground">Unsaved changes</span> : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
